import * as React from "react";

const MOBILE_BREAKPOINT = 768;
const FORCE_DESKTOP_KEY = 'fw.forceDesktop.v1';

// #41: Allow the user to opt out of mobile shell ("View desktop site").
// Flag lives in localStorage so it survives navigation; toggling it
// dispatches a window event so any mounted useIsMobile() re-renders.
export function isForcingDesktop(): boolean {
  try {
    return localStorage.getItem(FORCE_DESKTOP_KEY) === '1';
  } catch {
    return false;
  }
}

export function setForcingDesktop(force: boolean): void {
  try {
    if (force) localStorage.setItem(FORCE_DESKTOP_KEY, '1');
    else localStorage.removeItem(FORCE_DESKTOP_KEY);
    window.dispatchEvent(new CustomEvent('fw-force-desktop-changed'));
  } catch {
    // ignore
  }
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (isForcingDesktop()) return false;
    // Check viewport width first
    if (window.innerWidth < MOBILE_BREAKPOINT) return true;
    // Also check screen width — catches iframes with desktop-width viewport on mobile devices
    if (window.screen?.width < MOBILE_BREAKPOINT) return true;
    return false;
  });

  React.useEffect(() => {
    const check = () => {
      if (isForcingDesktop()) {
        setIsMobile(false);
        return;
      }
      const viewportMobile = window.innerWidth < MOBILE_BREAKPOINT;
      const screenMobile = (window.screen?.width ?? Infinity) < MOBILE_BREAKPOINT;
      setIsMobile(viewportMobile || screenMobile);
    };
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    mql.addEventListener("change", check);
    window.addEventListener('fw-force-desktop-changed', check);
    check();
    return () => {
      mql.removeEventListener("change", check);
      window.removeEventListener('fw-force-desktop-changed', check);
    };
  }, []);

  return isMobile;
}

/** Returns true when the device has a touch screen (works in both portrait and landscape). */
export function useIsTouchDevice() {
  const [isTouch, setIsTouch] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  });

  React.useEffect(() => {
    const check = () =>
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    setIsTouch(check());
  }, []);

  return isTouch;
}

export type Orientation = 'portrait' | 'landscape';

/** Returns the current device orientation ('portrait' or 'landscape'). Updates on rotation. */
export function useOrientation(): Orientation {
  const [orientation, setOrientation] = React.useState<Orientation>(
    () => (typeof window !== 'undefined' && window.innerWidth > window.innerHeight) ? 'landscape' : 'portrait'
  );

  React.useEffect(() => {
    const mql = window.matchMedia('(orientation: landscape)');
    const onChange = (e: MediaQueryListEvent) => setOrientation(e.matches ? 'landscape' : 'portrait');
    setOrientation(mql.matches ? 'landscape' : 'portrait');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return orientation;
}
